import { styled } from '@linaria/react';
import { JSX } from 'react';
import type { IntegrationEntry } from '../../../../api/integrations-client/integrations-client.type';
import { IntegrationType } from '../../../../api/integrations-client/integrations-client.type';
import { primaryBase, redBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { UNKNOWN_SUBPROJECT_NAME } from '../board-settings-tab/board-settings.helpers';
import { formatDatetimeToLocal } from '../data-management.helpers';
import type { DataSourceTableRow } from './data-sources-panel.type';
import { isNeverIngestedDate, isUpToDate } from './data-sources.helpers';

/**
 * Returns a styled icon component indicating whether data is up to date
 *
 * @param {boolean} upToDate - Boolean indicating if the data source is up to date
 * @returns {JSX.Element} - Icon component with check or cancel icon in appropriate color
 */

const getUpToDateIcon = (upToDate: boolean): JSX.Element => {
  return (
    <Icon name={upToDate ? 'check_circle_outlined' : 'cancel'} size={24} color={upToDate ? primaryBase : redBase} />
  );
};

/**
 * Groups integration data into table rows keyed by integration type.
 *
 * Responsibilities:
 * 1. For entries that carry a `repositories` array (SCM-style integrations
 *    such as GitHub), emit one row per repository, deriving `lastUpdated`
 *    from `repo.project_last_completed_at` and using "-" as the sentinel
 *    for never-ingested repos so the "Integrated Only" toggle in the
 *    consuming panel can hide them
 * 2. For entries without repositories, emit one row per project, deriving
 *    `boards` from `entry.subprojects` filtered by `project.subproject_ids`
 * 3. Dedupe project/repo ids per `integration_type` so a project that
 *    belongs to multiple integration types surfaces under each section,
 *    while duplicate entries from multiple users collapse within a type
 * 4. Populate `linkedProjectId` separately from `id` — equal to `id` for
 *    project-style rows, equal to `repo.project_id` for repo-style rows
 *    (undefined when the repo has no linked project) — so consumers can
 *    key project-scoped endpoints (e.g. audit-log sparkline) without
 *    being broken by repo-style rows whose `id` is the repo's own id
 *
 * @param {IntegrationEntry[]} integrationEntries - Array of integration entries
 * @param {Set<string>} expandedRows - Row keys (`${entry.id}-${project|repo.id}`) currently expanded
 * @returns {Record<IntegrationType, DataSourceTableRow[]>} Rows grouped by integration type
 */
const createIntegrationTypeData = (
  integrationEntries: IntegrationEntry[],
  expandedRows: Set<string>,
): Record<IntegrationType, DataSourceTableRow[]> => {
  const resultByType: Record<IntegrationType, DataSourceTableRow[]> = {} as Record<
    IntegrationType,
    DataSourceTableRow[]
  >;
  // Track seen ids per integration_type so the same project can still surface
  // under each integration it belongs to (e.g. Jira + GitHub). Within a single
  // integration_type, dedupe collapses entries from multiple users that
  // reference the same project/repo.
  const seenProjectIdsByType: Partial<Record<IntegrationType, Set<string>>> = {};
  const seenRepoIdsByType: Partial<Record<IntegrationType, Set<string>>> = {};

  integrationEntries.forEach((entry) => {
    // SCM-style integrations (e.g. GitHub) ship a `repositories` array — emit
    // one row per repo using `project_last_completed_at` as `lastUpdated`, and
    // the "-" sentinel when never ingested so the "Integrated Only" toggle in
    // the consuming panel (which filters `row.lastUpdated !== '-'`) can hide
    // them. Project rows keep the legacy "N/A" sentinel and are not affected
    // by that toggle.
    if (entry.repositories && entry.repositories.length > 0) {
      const seenRepoIds = (seenRepoIdsByType[entry.integration_type] ??= new Set<string>());
      entry.repositories.forEach((repo) => {
        if (seenRepoIds.has(repo.id)) {
          return;
        }
        seenRepoIds.add(repo.id);

        const projectLastCompletedAt = repo.project_last_completed_at;
        const lastUpdated =
          !projectLastCompletedAt || isNeverIngestedDate(projectLastCompletedAt)
            ? '-'
            : formatDatetimeToLocal(projectLastCompletedAt);

        const repoRow: DataSourceTableRow = {
          key: `${entry.id}-${repo.id}`,
          id: repo.id,
          // Track the linked project id separately from `id` so the audit-log
          // sparkline (keyed by project_id) can resolve activity for this row.
          // `repo.project_id` is nullable; the consuming panel skips rows
          // without one, which keeps the existing "No data" rendering intact.
          linkedProjectId: repo.project_id ?? undefined,
          projectName: repo.name,
          totalBoards: 1,
          owner: entry.user_name || 'N/A',
          lastUpdated,
          upToDate: getUpToDateIcon(isUpToDate(projectLastCompletedAt)),
          boards: [],
          isExpanded: expandedRows.has(`${entry.id}-${repo.id}`),
          searchableText: repo.name.toLowerCase(),
        };

        if (!resultByType[entry.integration_type]) {
          resultByType[entry.integration_type] = [];
        }
        resultByType[entry.integration_type].push(repoRow);
      });
      return;
    }

    const seenProjectIds = (seenProjectIdsByType[entry.integration_type] ??= new Set<string>());
    entry.projects.forEach((project) => {
      if (seenProjectIds.has(project.id)) {
        return;
      }
      seenProjectIds.add(project.id);

      const uniqueSubprojectIds = Array.from(new Set(project.subproject_ids));

      // Filter and deduplicate boards by unique id
      const seenBoardIds = new Set<string>();
      const boards = entry.subprojects.filter((sub) => {
        const isIncluded = uniqueSubprojectIds.includes(sub.id);
        const isUnique = !seenBoardIds.has(sub.id);
        if (isIncluded && isUnique) {
          seenBoardIds.add(sub.id);
          return true;
        }
        return false;
      });

      const tableRow: DataSourceTableRow = {
        key: `${entry.id}-${project.id}`,
        id: project.id,
        linkedProjectId: project.id,
        projectName: project.name,
        totalBoards: uniqueSubprojectIds.length,
        owner: entry.user_name || 'N/A',
        lastUpdated: project.last_completed_at ? formatDatetimeToLocal(project.last_completed_at) : 'N/A',
        upToDate: getUpToDateIcon(isUpToDate(project.last_completed_at)),
        boards,
        isExpanded: expandedRows.has(`${entry.id}-${project.id}`),
        searchableText: project.name.toLowerCase(),
      };

      if (!resultByType[entry.integration_type]) {
        resultByType[entry.integration_type] = [];
      }
      resultByType[entry.integration_type].push(tableRow);
    });
  });

  return resultByType;
};

/**
 * Filters table data based on search term
 *
 * @param {DataSourceTableRow[]} data - Array of table rows
 * @param {string} search - Search term
 * @returns {DataSourceTableRow[]} - Filtered array of table rows
 */
const filterTableData = (data: DataSourceTableRow[], search: string): DataSourceTableRow[] => {
  const tableData: DataSourceTableRow[] = [];
  const seenBoardKeys = new Set<string>(); // Track board keys to prevent duplicates

  data.forEach((project) => {
    // Skip board rows - they should already be in the data if filtering was done before
    if (project.isBoard) {
      // If we encounter a board row, add it to tableData but track its key to prevent duplicates
      const boardKey = project.key;
      if (!seenBoardKeys.has(boardKey)) {
        seenBoardKeys.add(boardKey);
        tableData.push(project);
      }
      return;
    }

    const normalizedSearch = search.toLowerCase().trim();
    const projectMatchesSearch = !normalizedSearch || project.searchableText?.includes(normalizedSearch);
    const boardMatchesSearch = project.boards.some((board) =>
      (board.name ?? '').toLowerCase().includes(normalizedSearch),
    );
    const shouldIncludeProject = !normalizedSearch || projectMatchesSearch || boardMatchesSearch;

    if (shouldIncludeProject) {
      tableData.push(project);
      if (project.isExpanded) {
        project.boards.forEach((board) => {
          const boardName = board.name ?? UNKNOWN_SUBPROJECT_NAME;
          // If project matches search, show all boards. Otherwise, filter boards by search.
          const searchCondition =
            !normalizedSearch || projectMatchesSearch || boardName.toLowerCase().includes(normalizedSearch);

          if (searchCondition) {
            const boardKey = `${project.key}-${board.id}`;

            // Prevent duplicate board rows
            if (seenBoardKeys.has(boardKey)) {
              return;
            }

            seenBoardKeys.add(boardKey);

            tableData.push({
              id: board.id,
              key: boardKey,
              projectName: (
                <BoardRow>
                  <Text weight="medium">{boardName}</Text>
                </BoardRow>
              ),
              totalBoards: null,
              owner: null,
              lastUpdated: formatDatetimeToLocal(board.last_data_fetch),
              upToDate: getUpToDateIcon(isUpToDate(board.last_data_fetch)),
              isBoard: true,
              parentId: project.key,
              boards: [],
              searchableText: boardName.toLowerCase(),
            });
          }
        });
      }
    }
  });

  return tableData;
};

const BoardRow = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  margin-top: -1px; // Remove the gap created by the border
`;

/**
 * Returns the number of active health check issues for an integration entry.
 * Returns 0 when health_issue_count is undefined or zero (backward compatible
 * with API responses that predate the health_issue_count field).
 *
 * @param {IntegrationEntry} entry - The integration entry
 * @returns {number} The count of active health issues, or 0
 */
const getHealthIssueBadgeCount = (entry: IntegrationEntry): number => {
  return entry.health_issue_count ?? 0;
};

export { createIntegrationTypeData, filterTableData, getHealthIssueBadgeCount };
